import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = 'https://pslcote-backend-alrq.onrender.com/api/contact';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    requirement: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(API_URL, formData);
      toast.success('Thank you! Your inquiry has been submitted successfully.');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        requirement: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_pslcote-labels/artifacts/uvw3rn59_ChatGPT%20Image%20Nov%2017%2C%202025%2C%2012_28_32%20PM.png" 
            alt="Contact PSLCOTE"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/30"></div>
        </div>
        <div className="section-container relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold text-white text-center mb-4 drop-shadow-lg" data-testid="contact-page-title">Contact Us</h1>
          <p className="text-lg lg:text-xl text-white text-center mx-auto max-w-3xl drop-shadow-md">
            Get in touch with our team to discuss your custom label stock requirements
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-6 pslcote-blue" data-testid="contact-info-title">Get In Touch</h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Our technical team is ready to collaborate with you on custom-engineered label stock solutions. Reach out to discuss your requirements, request samples, or schedule a facility visit.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <Phone className="text-[#0066cc]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Phone</h3>
                    <div className="space-y-1">
                      <p className="text-gray-600">
                        <span className="font-medium">Landline:</span>{' '}
                        <a href="tel:02522297551" className="hover:text-[#0066cc] transition-colors" data-testid="contact-landline">
                          0252-2297551 / 52
                        </a>
                      </p>
                      <p className="text-gray-600">
                        <span className="font-medium">Mobile:</span>{' '}
                        <a href="tel:+919820308238" className="hover:text-[#0066cc] transition-colors" data-testid="contact-mobile">
                          +91 98203 08238
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <Mail className="text-[#0066cc]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <a href="mailto:sreekumar@pslcote.com" className="text-gray-600 hover:text-[#0066cc] transition-colors" data-testid="contact-email">
                      sreekumar@pslcote.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <MapPin className="text-[#0066cc]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Location</h3>
                    <p className="text-gray-600 leading-relaxed" data-testid="contact-location">
                      Navagraha Print & Pack Co.Pvt.Ltd.<br />
                      BLDG. No. 303, F/4, Gala No. 1 & 2,<br />
                      Shree Sadgurukrupa,<br />
                      Warehouse Shree Rajlaxmi Com. Survey No. 74/6,<br />
                      Vadpe, Bhiwandi.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-3">Business Hours</h3>
                <div className="space-y-2 text-gray-600 text-sm">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span className="font-medium">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6" data-testid="consultation-form-title">Request a Consultation</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0066cc] focus:border-transparent"
                      placeholder="Your full name"
                      data-testid="form-input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0066cc] focus:border-transparent"
                      placeholder="Your company name"
                      data-testid="form-input-company"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0066cc] focus:border-transparent"
                      placeholder="your.email@company.com"
                      data-testid="form-input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0066cc] focus:border-transparent"
                      placeholder="+91 XXXXXXXXXX"
                      data-testid="form-input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="requirement" className="block text-sm font-medium text-gray-700 mb-2">
                      Requirement Description *
                    </label>
                    <textarea
                      id="requirement"
                      name="requirement"
                      value={formData.requirement}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0066cc] focus:border-transparent resize-none"
                      placeholder="Describe your label stock requirements, application, substrate, printing method, etc."
                      data-testid="form-input-requirement"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0066cc] text-white py-3 rounded-lg font-semibold hover:bg-[#0052a3] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="form-submit-btn"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send size={20} />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-100 py-16">
        <div className="section-container">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="aspect-video rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3765.1234567890!2d73.0000000!3d19.3000000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDE4JzAwLjAiTiA3M8KwMDAnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin&q=Shree+Sadgurukrupa+Warehouse+Shree+Rajlaxmi+Com+Survey+No+74/6+Vadpe+Bhiwandi"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="PSLCOTE Location Map"
                data-testid="contact-map"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;